import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  CheckCircle, 
  ArrowRight, 
  Star, 
  Zap,
  BookOpen,
  TrendingUp,
  Users,
  Gift,
  ChevronDown
} from 'lucide-react';
import { trackCTAClick, trackExternalLink } from '@/utils/analytics';

const NuRataAsta = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCTAClick = (location) => {
    // Track the click in Google Analytics
    trackCTAClick('stan_store_cta', location);
    trackExternalLink('https://stan.store/FinanciallyFreeOfficial', 'Stan Store');
    
    // Open Stan Store
    window.open('https://stan.store/FinanciallyFreeOfficial', '_blank');
  };

  const benefits = [
    {
      icon: <BookOpen className="h-6 w-6" />,
      title: "Ghiduri practice",
      desc: "Nu teorie, ci pași concreți pe care îi aplici azi"
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "Strategii testate",
      desc: "Același sistem folosit de sute de români"
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Rezultate rapide",
      desc: "Vezi progres în primele 30 de zile"
    }
  ];

  const testimonials = [
    {
      name: "Maria D.",
      role: "Freelancer, București",
      text: "Am economisit 3.000€ în 6 luni. Nu credeam că e posibil!",
      rating: 5
    },
    {
      name: "Alex P.",
      role: "IT, Cluj",
      text: "În sfârșit cineva care explică pe înțelesul tuturor.",
      rating: 5
    },
    {
      name: "Andreea M.",
      role: "Marketing, Timișoara", 
      text: "Cel mai bun investment în educația mea financiară.",
      rating: 5
    }
  ];

  const products = [
    {
      title: "Pachet de pornire",
      desc: "Dă-i bătaie călătoriei tale spre libertatea financiară",
      price: "GRATUIT",
      highlight: true
    },
    {
      title: "Înțelege Blockchain-ul",
      desc: "Ghid PDF de 8 pagini explicat ca la o bere",
      price: "3.99€"
    },
    {
      title: "📘 eBook Complet",
      desc: "Primești imediat în inbox, citești în 2 minute",
      price: "17€"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Mobile First */}
      <section className="relative min-h-[100svh] flex flex-col justify-center px-4 py-8 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800" />
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-0 w-48 h-48 bg-mint-400/20 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-lg mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
            <Gift className="h-4 w-4" />
            <span>Produs gratuit disponibil</span>
          </div>
          
          {/* Main headline */}
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight">
            Vrei libertate 
            <span className="text-mint-300"> financiară</span>?
          </h1>
          
          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-white/90 mb-8 leading-relaxed">
            Scapă de programul 9-17 și începe să-ți construiești viitorul.
            <br />
            <strong>Fără scheme. Doar ce funcționează.</strong>
          </p>
          
          {/* Primary CTA */}
          <Button 
            onClick={() => handleCTAClick('hero_section')}
            className="w-full sm:w-auto bg-mint-400 hover:bg-mint-500 text-slate-900 font-bold text-lg px-8 py-6 rounded-xl shadow-2xl shadow-mint-400/30 transform hover:scale-105 transition-all duration-300"
          >
            Ia Pachetul Gratuit
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          
          {/* Trust indicators */}
          <div className="flex items-center justify-center gap-6 mt-8 text-white/80 text-sm">
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              <span>2.500+ membri</span>
            </div>
            <div className="flex items-center gap-1">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span>4.9/5 rating</span>
            </div>
          </div>
          
          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className="h-8 w-8 text-white/60" />
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="bg-slate-900 py-4 px-4">
        <div className="max-w-lg mx-auto">
          <p className="text-center text-white/90 text-sm">
            🇷🇴 Creat de români, pentru români
          </p>
        </div>
      </section>

      {/* Problem/Agitation Section */}
      <section className="py-12 px-4 bg-slate-50">
        <div className="max-w-lg mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6">
            Te regăsești?
          </h2>
          
          <div className="space-y-4 text-left">
            {[
              "Nu știi pe ce se duc banii",
              "Vrei să economisești dar nu-ți iese",
              "Auzi de investiții dar pare complicat",
              "Simți că toți ceilalți o duc mai bine"
            ].map((item, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm"
              >
                <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-red-500 font-bold">✗</span>
                </div>
                <p className="text-slate-700">{item}</p>
              </div>
            ))}
          </div>
          
          <p className="mt-8 text-lg text-slate-600">
            <strong className="text-slate-900">Nu ești singur.</strong> Și există o soluție.
          </p>
        </div>
      </section>

      {/* Solution Section with Image */}
      <section className="py-12 px-4">
        <div className="max-w-lg mx-auto">
          {/* Image */}
          <div className="relative rounded-2xl overflow-hidden mb-8 shadow-xl">
            <img 
              src="https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=800&q=80"
              alt="Învățare financiară"
              className="w-full h-48 sm:h-64 object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <p className="text-white font-semibold text-lg">
                Simplu ca bună ziua.
              </p>
            </div>
          </div>
          
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
            Ce primești?
          </h2>
          
          <div className="space-y-4">
            {benefits.map((benefit, idx) => (
              <div 
                key={idx}
                className="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-mint-50 rounded-xl"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-blue-600">
                  {benefit.icon}
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">{benefit.title}</h3>
                  <p className="text-slate-600 text-sm">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Preview */}
      <section className="py-12 px-4 bg-slate-900">
        <div className="max-w-lg mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 text-center">
            Produsele noastre
          </h2>
          <p className="text-white/70 text-center mb-8">
            De la gratuit la premium
          </p>
          
          <div className="space-y-4">
            {products.map((product, idx) => (
              <Card 
                key={idx}
                className={`p-4 ${product.highlight 
                  ? 'bg-gradient-to-r from-mint-400 to-mint-500 border-none' 
                  : 'bg-white/10 border-white/20'}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`font-bold ${product.highlight ? 'text-slate-900' : 'text-white'}`}>
                      {product.title}
                    </h3>
                    <p className={`text-sm ${product.highlight ? 'text-slate-700' : 'text-white/70'}`}>
                      {product.desc}
                    </p>
                  </div>
                  <div className={`font-bold text-lg ${product.highlight ? 'text-slate-900' : 'text-mint-400'}`}>
                    {product.price}
                  </div>
                </div>
                {product.highlight && (
                  <div className="mt-3 flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle className="h-4 w-4" />
                    <span>Disponibil acum</span>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-lg mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 text-center">
            Ce zic alții
          </h2>
          <p className="text-slate-600 text-center mb-8">
            Oameni reali, rezultate reale
          </p>
          
          <div className="space-y-4">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="p-5 bg-slate-50 border-none">
                {/* Stars */}
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                
                {/* Quote */}
                <p className="text-slate-700 mb-4 italic">
                  "{testimonial.text}"
                </p>
                
                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-mint-500 rounded-full flex items-center justify-center text-white font-bold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{testimonial.name}</p>
                    <p className="text-slate-500 text-xs">{testimonial.role}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 relative overflow-hidden">
        {/* Decorative */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-mint-400/20 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-lg mx-auto text-center">
          {/* Urgency badge */}
          <div className="inline-flex items-center gap-2 bg-mint-400 text-slate-900 text-sm font-bold px-4 py-2 rounded-full mb-6">
            <Zap className="h-4 w-4" />
            <span>Gratuit pentru o perioadă limitată</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Gata să începi?
          </h2>
          
          <p className="text-lg text-white/90 mb-8">
            Ia pachetul gratuit și fă primul pas spre libertatea financiară.
          </p>
          
          {/* Main CTA */}
          <Button 
            onClick={() => handleCTAClick('final_cta_section')}
            className="w-full bg-mint-400 hover:bg-mint-500 text-slate-900 font-bold text-lg px-8 py-6 rounded-xl shadow-2xl shadow-mint-400/30 transform hover:scale-105 transition-all duration-300 mb-4"
          >
            <Gift className="mr-2 h-5 w-5" />
            Vreau Pachetul Gratuit
          </Button>
          
          {/* Reassurance */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-white/80 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-mint-400" />
              <span>100% gratuit</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-mint-400" />
              <span>Fără card</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-mint-400" />
              <span>Acces instant</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-slate-900">
        <div className="max-w-lg mx-auto text-center">
          <p className="text-white font-bold text-lg mb-2">financiallyfree.ro</p>
          <p className="text-white/60 text-sm mb-4">
            Libertate financiară, fără să-ți explodeze capul.
          </p>
          <div className="flex justify-center gap-4 text-white/40 text-xs">
            <a href="/privacy" className="hover:text-white/60">Confidențialitate</a>
            <span>•</span>
            <a href="/terms" className="hover:text-white/60">Termeni</a>
          </div>
        </div>
      </footer>

      {/* Sticky Bottom CTA - Mobile */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur-sm border-t border-slate-200 sm:hidden z-50">
        <Button 
          onClick={() => handleCTAClick('sticky_mobile_cta')}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl"
        >
          <Gift className="mr-2 h-5 w-5" />
          Ia Gratuit Acum
        </Button>
      </div>
      
      {/* Bottom padding for sticky CTA on mobile */}
      <div className="h-20 sm:hidden" />
    </div>
  );
};

export default NuRataAsta;
